import Slider from '@components/Slider/Slider';
import { useImageVerification } from '@hooks/api';
import stars from '@static/vectors/sentinel/stars.svg';
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useAppSelector } from 'universal-scripts';
import ImageInfoDialog from '../ImageInfoDialog/ImageInfoDialog';
import ImageDialog from '../ImageModal/ImageDialog';
import './ImageVerificator.css';

const ITEMS_PER_SLIDE = 4;

export default function ImageVerificator() {
	const keyframes = useAppSelector((state) => state.sentinel.phases.detect.keyframes);
	const result = useImageVerification(keyframes);

	const [zoomedImage, setZoomedImage] = useState<string | null>(null);
	const [isInfoOpen, setIsInfoOpen] = useState(false);

	const verifications = useMemo(() => {
		return result?.verifications || [];
	}, [result]);

	const aiGeneratedImages = useMemo(() => verifications.filter((item) => item.isAiGenerated), [verifications]);

	const slides = useMemo(() => {
		const chunks = [];
		for (let i = 0; i < aiGeneratedImages.length; i += ITEMS_PER_SLIDE) {
			chunks.push(aiGeneratedImages.slice(i, i + ITEMS_PER_SLIDE));
		}
		return chunks;
	}, [aiGeneratedImages]);

	if (!result) return null;

	if (aiGeneratedImages.length === 0) {
		return (
			<h4 className="no-ai-images">
				<span className="icon icon-image" />
				<span>
					<FormattedMessage id="sentinel.image-verification.no-ai" />
				</span>
			</h4>
		);
	}

	return (
		<div className="image-verificator">
			<h2>
				<img src={stars} alt="stars icon" />
				<strong>
					<FormattedMessage id="sentinel.image-verification.ai-generated" /> ({aiGeneratedImages.length}/{verifications.length})
				</strong>

				<button className="info-button-trigger" onClick={() => setIsInfoOpen(true)}>
					<span className="icon icon-info">i</span>
				</button>
			</h2>

			<Slider hasIndicators={false}>
				{slides.map((group, slideIndex) => (
					<div className="image-verification-card" key={`slide-${slideIndex}`}>
						{group.map((item) => {
							return (
								<div
									key={item.url}
									className="image-wrapper clickable"
									style={{ backgroundImage: `url(${item.url})` }}
									role="button"
									tabIndex={0}
									onClick={() => setZoomedImage(item.url)}
									onKeyDown={(e) => {
										if (e.key === 'Enter' || e.key === ' ') {
											e.preventDefault();
											setZoomedImage(item.url);
										}
									}}
								>
									<div className="badge badge-ai">
										<span>
											<FormattedMessage id="sentinel.image-verification.ai-generated" />
										</span>
										<strong className="value"> - {item.confidence.toFixed(0)}%</strong>
									</div>
								</div>
							);
						})}
					</div>
				))}
			</Slider>
			<ImageDialog isOpen={!!zoomedImage} imageUrl={zoomedImage || ''} onClose={() => setZoomedImage(null)} />
			<ImageInfoDialog isOpen={isInfoOpen} onClose={() => setIsInfoOpen(false)} data={result} />
			<em className="powered-box">
				<span>Powered by</span>
				<a target="_blank" href={result.provider.url} rel="noreferrer">
					{result.provider.image ? (
						<img src={result.provider.image} alt={result.provider.name} />
					) : (
						<strong>{result.provider.name || result.provider.url}</strong>
					)}
				</a>
			</em>
		</div>
	);
}
